/**
 * Key-value storage behind the save system (architecture §4.1). The browser uses `localStorage`; tests and debug sessions
 * (`?save=memory`, `?fixture=`, `?seed=`) use an in-memory map so captures can never touch the player's save (§4.4).
 */

export interface KeyValueStorage {
  getItem(key: string): string | null;
  /** Throws {@link StorageFullError} when the quota is exceeded. */
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
  /** Every key currently stored. */
  keys(): string[];
  /** 'local' = window.localStorage, 'memory' = an in-memory map. */
  readonly kind: 'local' | 'memory';
}

/** The quota was exceeded while writing (ui-ux `simulate=storage-full`). */
export class StorageFullError extends Error {
  constructor(message = 'storage full') {
    super(message);
    this.name = 'StorageFullError';
  }
}

function isQuotaError(e: unknown): boolean {
  if (!(e instanceof Error)) return false;
  return e.name === 'QuotaExceededError' || e.name === 'NS_ERROR_DOM_QUOTA_REACHED' || /quota/i.test(e.message);
}

/** An in-memory storage (optionally pre-filled). `quotaBytes` simulates a full disk. */
export function memoryStorage(init: Record<string, string> = {}, opts: { quotaBytes?: number } = {}): KeyValueStorage {
  const map = new Map<string, string>(Object.entries(init));
  const used = (): number => { let n = 0; for (const [k, v] of map) n += (k.length + v.length) * 2; return n; };
  return {
    kind: 'memory',
    getItem: (k) => map.get(k) ?? null,
    setItem: (k, v) => {
      if (opts.quotaBytes !== undefined) {
        const prev = map.get(k);
        const next = used() - (prev ? (k.length + prev.length) * 2 : 0) + (k.length + v.length) * 2;
        if (next > opts.quotaBytes) throw new StorageFullError();
      }
      map.set(k, v);
    },
    removeItem: (k) => { map.delete(k); },
    keys: () => [...map.keys()],
  };
}

/** `window.localStorage` wrapped (null when it is unavailable, e.g. blocked cookies). */
export function browserStorage(): KeyValueStorage | null {
  let ls: Storage;
  try {
    ls = window.localStorage;
    const probe = '__marg_probe__';
    ls.setItem(probe, '1');
    ls.removeItem(probe);
  } catch {
    return null;
  }
  return {
    kind: 'local',
    getItem: (k) => { try { return ls.getItem(k); } catch { return null; } },
    setItem: (k, v) => {
      try { ls.setItem(k, v); } catch (e) { if (isQuotaError(e)) throw new StorageFullError(); throw e; }
    },
    removeItem: (k) => { try { ls.removeItem(k); } catch { /* ignore */ } },
    keys: () => { const out: string[] = []; for (let i = 0; i < ls.length; i++) { const k = ls.key(i); if (k !== null) out.push(k); } return out; },
  };
}
